import { defineMessages } from '@formatjs/intl';

const messages = defineMessages({
  VALIDATION__AlternateWorkoutDifferent: {
    id: 'validations.VALIDATION__AlternateWorkoutDifferent',
    defaultMessage:
      'An alternate workout must be different from the primary workout.',
  },
  VALIDATION__ConfirmationPasswordMustMatch: {
    id: 'validations.VALIDATION__ConfirmationPasswordMustMatch',
    defaultMessage: 'Passwords do not match.',
  },
  VALIDATION__TenantNotFound: {
    id: 'validations.VALIDATION__TenantNotFound',
    defaultMessage: 'Tenant not found.',
  },
  VALIDATION__InvalidInstagramUrl: {
    id: 'validations.VALIDATION__InvalidInstagramUrl',
    defaultMessage: 'Invalid Instagram URL.',
  },
  VALIDATION__InvalidFacebookUrl: {
    id: 'validations.VALIDATION__InvalidFacebookUrl',
    defaultMessage: 'Invalid Facebook URL.',
  },
  VALIDATION__InvalidTwitterUrl: {
    id: 'validations.VALIDATION__InvalidTwitterUrl',
    defaultMessage: 'Invalid X URL.',
  },
  VALIDATION__InvalidLinkedInUrl: {
    id: 'validations.VALIDATION__InvalidLinkedInUrl',
    defaultMessage: 'Invalid LinkedIn URL.',
  },
  VALIDATION__InvalidTikTokUrl: {
    id: 'validations.VALIDATION__InvalidTikTokUrl',
    defaultMessage: 'Invalid TikTok URL.',
  },
  VALIDATION__InvalidHexColor: {
    id: 'validations.VALIDATION__InvalidHexColor',
    defaultMessage: 'Invalid color — must be a #RRGGBB hex value.',
  },
  VALIDATION__Unauthenticated: {
    id: 'validations.VALIDATION__Unauthenticated',
    defaultMessage: 'Not authenticated.',
  },
  VALIDATION__Max255CharLimit: {
    id: 'validations.VALIDATION__Max255CharLimit',
    defaultMessage: 'Limit of 255 character exceeded.',
  },
  VALIDATION__Max6LinesLimit: {
    id: 'validations.VALIDATION__Max6LinesLimit',
    defaultMessage: 'Use at most 6 lines.',
  },
  // `zod.filter.ts` looks a zod message up in THIS object by name, so a schema
  // that names a key missing from here does not fall back to its own string —
  // `TranslationService.formatMessage` catches the undefined descriptor and
  // answers "Unknown, message could not be translated". That is what a rejected
  // `StoredAssetName` used to render as.
  VALIDATION__InvalidAssetName: {
    id: 'validations.VALIDATION__InvalidAssetName',
    defaultMessage: 'This file name is not allowed. Upload the image again.',
  },
  // Same defect as the key above, in `QueryAllDto`'s comma-separated id lists
  // (`ids`, `tagIds`, `equipmentTagIds`, …): the refine named an English
  // sentence instead of a key, so a rejected list rendered as "Unknown, message
  // could not be translated" in every locale.
  VALIDATION__InvalidIdList: {
    id: 'validations.VALIDATION__InvalidIdList',
    defaultMessage: 'This filter must be a comma-separated list of ids.',
  },
  VALIDATION__MaxPasswordCharLimit: {
    id: 'validations.VALIDATION__MaxPasswordCharLimit',
    defaultMessage: 'Maximum 64 characters allowed.',
  },
  VALIDATION__Max30CharLimit: {
    id: 'validations.VALIDATION__Max30CharLimit',
    defaultMessage: 'Maximum 30 characters allowed.',
  },
  VALIDATION__InvalidUsername: {
    id: 'validations.VALIDATION__InvalidUsername',
    defaultMessage:
      'Use only lowercase letters, numbers, dots, underscores and hyphens.',
  },
  VALIDATION__Min3CharLimit: {
    id: 'validations.VALIDATION__Min3CharLimit',
    defaultMessage: 'Minimum 3 characters required.',
  },
  VALIDATION__Min8CharLimit: {
    id: 'validations.VALIDATION__Min8CharLimit',
    defaultMessage: 'Minimum 8 characters required.',
  },
  VALIDATION__Max40CharLimit: {
    id: 'validations.VALIDATION__Max40CharLimit',
    defaultMessage: 'Maximum 40 characters allowed.',
  },
  VALIDATION__Max35CharLimit: {
    id: 'validations.VALIDATION__Max35CharLimit',
    defaultMessage: 'Maximum 35 characters allowed.',
  },
  VALIDATION__Max20CharLimit: {
    id: 'validations.VALIDATION__Max20CharLimit',
    defaultMessage: 'Maximum 20 characters allowed.',
  },
  VALIDATION__InvalidSlug: {
    id: 'validations.VALIDATION__InvalidSlug',
    defaultMessage:
      'Use only lowercase letters, numbers, and single hyphens between words.',
  },
  VALIDATION__ReservedSlug: {
    id: 'validations.VALIDATION__ReservedSlug',
    defaultMessage: 'This word is reserved and cannot be used here.',
  },
  VALIDATION__Min16Age: {
    id: 'validations.VALIDATION__Min16Age',
    defaultMessage: 'Minimum age is 16.',
  },
  VALIDATION__InvalidEmail: {
    id: 'validations.VALIDATION__InvalidEmail',
    defaultMessage: 'Please enter valid email address.',
  },
  VALIDATION__EmailOrPhoneRequired: {
    id: 'validations.VALIDATION__EmailOrPhoneRequired',
    defaultMessage: 'Enter an email or a phone number',
  },
  // What a dropzone answers when it refuses the file OUTRIGHT, before any
  // consumer's validator runs. react-dropzone filters by `accept` first and
  // hands `onDrop` an EMPTY accepted list, so the validator is called with
  // `undefined` and every consumer replies with its own "… is required" —
  // telling the user to choose a file they just chose, and saying nothing about
  // the only thing that was wrong with it.
  VALIDATION__UnsupportedFileType: {
    id: 'validations.VALIDATION__UnsupportedFileType',
    defaultMessage: 'This file type is not supported. Choose another file.',
  },
  // The SERVER-side twin of the message above, and a different refusal: the
  // browser asks for an upload URL and names the media type, and this is what
  // comes back when that type cannot produce a storable filename — an
  // `image/svg+xml` would mint `<uuid>.svg+xml`, whose `+` the stored-name rule
  // rejects, so the upload proof would silently skip it and the row would be
  // saved pointing at nothing.
  VALIDATION__UnsupportedMediaType: {
    id: 'validations.VALIDATION__UnsupportedMediaType',
    defaultMessage: 'This media type is not supported. Choose another file.',
  },
  // The content presign chokepoint's own refusal: `target` arrives as a raw
  // `@Query('target')` and anything other than `cover` or absent is unknown.
  // Silently falling back to the private presign would store a "public"
  // upload where no public reader will ever find it, so this is a 400, not a
  // quiet default.
  VALIDATION__UnknownUploadTarget: {
    id: 'validations.VALIDATION__UnknownUploadTarget',
    defaultMessage: 'Unknown upload target. Choose another file.',
  },
  // Storage would not answer — a throttle, a 5xx, a timeout. NOT the client's
  // fault and NOT "upload it again", which is advice that cannot work, so it
  // says what is true and what to do. It also replaces a raw storage error
  // whose text carried the internal object key straight to the client.
  VALIDATION__StorageUnavailable: {
    id: 'validations.VALIDATION__StorageUnavailable',
    defaultMessage:
      'File storage is temporarily unavailable. Please try again in a moment.',
  },
  // HTTP 429 from a backend throttle bucket (`_globals/throttling.ts`), set by the
  // frontend's `TooManyRequestsError` whatever the backend said. Names no duration on
  // purpose: one bucket blocks for a minute, another for an hour.
  VALIDATION__TooManyRequests: {
    id: 'validations.VALIDATION__TooManyRequests',
    defaultMessage: 'Too many attempts. Try again later.',
  },
  // HTTP 403 from the edge (Cloud Armor's WAF), whose HTML page carries no sentence: set
  // by the frontend's `handleErrors` for a 403 that is not the API's JSON envelope.
  VALIDATION__RequestBlocked: {
    id: 'validations.VALIDATION__RequestBlocked',
    defaultMessage:
      'Our security filter blocked this request. Change the text and try again.',
  },
  // The dropzone takes ONE file (`multiple: false`), and react-dropzone answers
  // a multi-file drop by rejecting ALL of them — so without its own message
  // this arrived as "… is required", i.e. the user was told to choose a file
  // immediately after choosing two.
  VALIDATION__OneFileOnly: {
    id: 'validations.VALIDATION__OneFileOnly',
    defaultMessage: 'Choose a single file.',
  },
  VALIDATION__FileTooLarge: {
    id: 'validations.VALIDATION__FileTooLarge',
    defaultMessage: 'This file is too large. Choose a smaller one.',
  },
  VALIDATION__FileTooSmall: {
    id: 'validations.VALIDATION__FileTooSmall',
    defaultMessage: 'This file is too small. Choose a larger one.',
  },
  // A presigned PUT happens between the browser and storage, so the server
  // never learns whether it succeeded. Storing the returned filename anyway is
  // taking the client's word for it, and when the word is wrong the row points
  // at nothing — a broken image under a success message. This is what the save
  // answers instead, and it names the one action that can actually help.
  VALIDATION__UploadNotFound: {
    id: 'validations.VALIDATION__UploadNotFound',
    defaultMessage: 'The uploaded file could not be found. Upload it again.',
  },
  // The stringified accidents of a broken value pipeline — 'NaN' from
  // `Number('80-130')`, 'null'/'undefined' from a value that was never there —
  // refused at the edge so a stale bundle cannot overwrite a prescription with
  // one. It names the field rather than the spelling because the spelling is
  // never what the trainer typed; the value they see is the one to clear.
  VALIDATION__InvalidConfigurableValue: {
    id: 'validations.VALIDATION__InvalidConfigurableValue',
    defaultMessage:
      'This value could not be read. Clear the field and enter it again.',
  },
  VALIDATION__CoverPhotoRequired: {
    id: 'validations.VALIDATION__CoverPhotoRequired',
    defaultMessage: 'Cover photo is required.',
  },
  VALIDATION__CoverPhotoAspectRatio: {
    id: 'validations.VALIDATION__CoverPhotoAspectRatio',
    defaultMessage: 'Cover photo must be in 16:9 aspect ration.',
  },
  VALIDATION__CoverPhotoDimensions: {
    id: 'validations.VALIDATION__CoverPhotoDimensions',
    defaultMessage: 'Cover photo must be at least 640x360 pixels.',
  },

  VALIDATION__LogoPhotoRequired: {
    id: 'validations.VALIDATION__LogoPhotoRequired',
    defaultMessage: 'Logo photo is required.',
  },
  VALIDATION__LogoPhotoAspectRatio: {
    id: 'validations.VALIDATION__LogoPhotoAspectRatio',
    defaultMessage: 'Logo photo must be in 16:9 or 1:1 (square) aspect ratio.',
  },
  VALIDATION__LogoPhotoDimensions: {
    id: 'validations.VALIDATION__LogoPhotoDimensions',
    defaultMessage: 'Logo photo must be at least 512x512 pixels.',
  },

  VALIDATION__EmailNotFound: {
    id: 'validations.VALIDATION__EmailNotFound',
    defaultMessage: 'Email not found.',
  },
  VALIDATION__InvalidCredentials: {
    id: 'validations.VALIDATION__InvalidCredentials',
    defaultMessage: 'Invalid credentials.',
  },
  VALIDATION__SetupPasswordTokenInvalid: {
    id: 'validations.VALIDATION__SetupPasswordTokenInvalid',
    defaultMessage: 'Token is invalid.',
  },
  VALIDATION__InvalidCountryCode: {
    id: 'validations.VALIDATION__InvalidCountryCode',
    defaultMessage: 'Invalid country code',
  },
  VALIDATION__ValueMustBeANumber: {
    id: 'validations.VALIDATION__ValueMustBeANumber',
    defaultMessage: 'Value must be a number.',
  },
  VALIDATION__WholeNumber: {
    id: 'validations.VALIDATION__WholeNumber',
    defaultMessage: 'The value must be a whole number.',
  },
  VALIDATION__Positive: {
    id: 'validations.VALIDATION__Positive',
    defaultMessage: 'The value must be greater than 0.',
  },
  VALIDATION__InvalidAmount: {
    id: 'validations.VALIDATION__InvalidAmount',
    defaultMessage: 'Enter a valid amount.',
  },
  VALIDATION__FieldNotAllowed: {
    id: 'validations.VALIDATION__FieldNotAllowed',
    defaultMessage: 'Field not allowed.',
  },
  VALIDATION__NotAllowed: {
    id: 'validations.VALIDATION__NotAllowed',
    defaultMessage: 'This value is not allowed.',
  },
  VALIDATION__DateInFuture: {
    id: 'validations.VALIDATION__DateInFuture',
    defaultMessage: 'The date cannot be in the future.',
  },
  VALIDATION__Max: {
    id: 'validations.VALIDATION__Max',
    defaultMessage: 'The value is above the allowed maximum.',
  },
  VALIDATION__MaxValue: {
    id: 'validations.VALIDATION__MaxValue',
    defaultMessage: 'The value is too large.',
  },
  VALIDATION__FieldsNotValid: {
    id: 'validations.VALIDATION__FieldsNotValid',
    defaultMessage: '{fields} not valid.',
  },
  VALIDATION__InternalServerError: {
    id: 'validations.VALIDATION__InternalServerError',
    defaultMessage: 'Internal server error.',
  },
  VALIDATION__Required: {
    id: 'validations.VALIDATION__Required',
    defaultMessage: 'Required.',
  },
  VALIDATION__InvalidDate: {
    id: 'validations.VALIDATION__InvalidDate',
    defaultMessage: 'Invalid date.',
  },
  VALIDATION__InvalidTimezone: {
    id: 'validations.VALIDATION__InvalidTimezone',
    defaultMessage: 'Invalid timezone.',
  },
  VALIDATION__InvalidDayOrDateTime: {
    id: 'validations.VALIDATION__InvalidDayOrDateTime',
    defaultMessage: 'Expected a day (yyyy-MM-dd) or a date and time.',
  },
  VALIDATION__TransferOrCopyNotBoth: {
    id: 'validations.VALIDATION__TransferOrCopyNotBoth',
    defaultMessage: 'Choose either transfer or copy for content, not both.',
  },
  VALIDATION__NotifyRecipientsRequired: {
    id: 'validations.VALIDATION__NotifyRecipientsRequired',
    defaultMessage: 'Choose who to notify when notifications are on.',
  },
  VALIDATION__AtLeastOneSite: {
    id: 'validations.VALIDATION__AtLeastOneSite',
    defaultMessage: 'Enter at least one measurement.',
  },
  VALIDATION__MaxRepsMustBeWhole: {
    id: 'validations.VALIDATION__MaxRepsMustBeWhole',
    defaultMessage: 'Max reps must be a whole number.',
  },
  VALIDATION__ZoneMinBelowMax: {
    id: 'validations.VALIDATION__ZoneMinBelowMax',
    defaultMessage: 'Each zone must have a minimum lower than its maximum.',
  },
  VALIDATION__ZonesMustIncrease: {
    id: 'validations.VALIDATION__ZonesMustIncrease',
    defaultMessage: 'Zones must increase from Z1 to Z5 without overlapping.',
  },
  VALIDATION__CustomActivityNeedsLabel: {
    id: 'validations.VALIDATION__CustomActivityNeedsLabel',
    defaultMessage: 'Enter a name for the custom activity.',
  },
  VALIDATION__GroceryOrFreeName: {
    id: 'validations.VALIDATION__GroceryOrFreeName',
    defaultMessage: 'Pick a grocery or enter a name — exactly one.',
  },
  VALIDATION__AtLeastOneField: {
    id: 'validations.VALIDATION__AtLeastOneField',
    defaultMessage: 'At least one field must be provided.',
  },
  VALIDATION__DeadlineMinimum24Hours: {
    id: 'validations.VALIDATION__DeadlineMinimum24Hours',
    defaultMessage: 'Deadline must be at least 24 hours from now.',
  },
  VALIDATION__AtLeastOneQuestion: {
    id: 'validations.VALIDATION__AtLeastOneQuestion',
    defaultMessage: 'At least 1 question must be in questionnaire.',
  },
  VALIDATION__InvalidRange: {
    id: 'validations.VALIDATION__InvalidRange',
    defaultMessage: 'The minimum must be lower than the maximum.',
  },
  VALIDATION__PasswordsMustMatch: {
    id: 'validations.VALIDATION__PasswordsMustMatch',
    defaultMessage: 'Passwords must match.',
  },
  VALIDATION__InvalidSocialMediaLinkType: {
    id: 'validations.VALIDATION__InvalidSocialMediaLinkType',
    defaultMessage: 'Invalid Social Media link type.',
  },
  VALIDATION__PhoneNumberRequired: {
    id: 'validations.VALIDATION__PhoneNumberRequired',
    defaultMessage: 'Phone number is required.',
  },
  VALIDATION__InvalidPhoneNumber: {
    id: 'validations.VALIDATION__InvalidPhoneNumber',
    defaultMessage: 'Please enter a valid phone number.',
  },
  VALIDATION__InvalidSource: {
    id: 'validations.VALIDATION__InvalidSource',
    defaultMessage: 'Invalid source.',
  },
  VALIDATION__InvalidSourceType: {
    id: 'validations.VALIDATION__InvalidSourceType',
    defaultMessage: 'Invalid source type.',
  },
  VALIDATION__InvalidTrainingLevel: {
    id: 'validations.VALIDATION__InvalidTrainingLevel',
    defaultMessage: 'Invalid training level.',
  },
  VALIDATION__FieldInvalid: {
    id: 'validations.VALIDATION__FieldInvalid',
    defaultMessage: 'The field {field} is invalid.',
  },
  VALIDATION__UniqueConstraint: {
    id: 'validations.VALIDATION__UniqueConstraint',
    defaultMessage: '{fields} must be unique.',
  },
  VALIDATION__DatabaseError: {
    id: 'validations.VALIDATION__DatabaseError',
    defaultMessage: 'A database error occurred.',
  },
  VALIDATION__ServiceUnavailable: {
    id: 'validations.VALIDATION__ServiceUnavailable',
    defaultMessage:
      'The service is temporarily unavailable. Please try again shortly.',
  },
  VALIDATION__InvalidForeignKey: {
    id: 'validations.VALIDATION__InvalidForeignKey',
    defaultMessage: 'The foreign key {foreignKey} is invalid.',
  },
  VALIDATION__NotNull: {
    id: 'validations.VALIDATION__NotNull',
    defaultMessage: '{fields} cannot be null.',
  },
  VALIDATION__ERR1001: {
    id: 'validations.VALIDATION__ERR1001',
    defaultMessage:
      'Internal server error. Try again later or contact support with error code ERR1001',
  },
  VALIDATION__NonNegative: {
    id: 'validations.VALIDATION__NonNegative',
    defaultMessage: 'The value must be greater than or equal to 0.',
  },
  VALIDATION__MaxValue5: {
    id: 'validations.VALIDATION__MaxValue5',
    defaultMessage: 'The value must be less than or equal to 5',
  },
  VALIDATION__InvalidLocale: {
    id: 'VALIDATION__InvalidLocale',
    defaultMessage: 'Invalid locale',
  },
  VALIDATION__InvalidRestTimes: {
    id: 'VALIDATION__InvalidRestTimes',
    defaultMessage: 'Enter valid number in format mm:ss (minutes/seconds)',
  },
  VALIDATION__Max1500CharLimit: {
    id: 'validations.VALIDATION__Max1500CharLimit',
    defaultMessage: 'Maximum 1500 characters allowed.',
  },
  VALIDATION__Max5000CharLimit: {
    id: 'validations.VALIDATION__Max5000CharLimit',
    defaultMessage: 'Maximum 5000 characters allowed.',
  },
  VALIDATION__InvalidUrl: {
    id: 'validations.VALIDATION__InvalidUrl',
    defaultMessage: 'Enter a valid URL.',
  },
  VALIDATION__DuplicateIds: {
    id: 'validations.VALIDATION__DuplicateIds',
    defaultMessage: 'The same item appears more than once.',
  },
  VALIDATION__Max120CharLimit: {
    id: 'validations.VALIDATION__Max120CharLimit',
    defaultMessage: 'Maximum 120 characters allowed.',
  },
  VALIDATION__DuplicateSocialType: {
    id: 'validations.VALIDATION__DuplicateSocialType',
    defaultMessage: 'Each network can be added only once.',
  },
  VALIDATION__PriceRangeReversed: {
    id: 'validations.VALIDATION__PriceRangeReversed',
    defaultMessage: 'The upper price must not be lower than the lower price.',
  },
  VALIDATION__DiscountExceedsPrice: {
    id: 'validations.VALIDATION__DiscountExceedsPrice',
    defaultMessage: 'The discount cannot be greater than the original price.',
  },
  VALIDATION__Max60CharLimit: {
    id: 'validations.VALIDATION__Max60CharLimit',
    defaultMessage: 'Maximum 60 characters allowed.',
  },
  VALIDATION__Max200CharLimit: {
    id: 'validations.VALIDATION__Max200CharLimit',
    defaultMessage: 'Maximum 200 characters allowed.',
  },
  VALIDATION__EndBeforeStart: {
    id: 'validations.VALIDATION__EndBeforeStart',
    defaultMessage: 'The end must be after the start.',
  },
  VALIDATION__OnsitePaymentStaffOnly: {
    id: 'validations.VALIDATION__OnsitePaymentStaffOnly',
    defaultMessage:
      'Paying at the desk is only available for a booking staff create on your behalf.',
  },
  // Task 5 (membership-renewal plan) — the used-up-choices dialog's "book
  // without membership" walk-in option.
  VALIDATION__IgnoreMembershipsStaffOnly: {
    id: 'validations.VALIDATION__IgnoreMembershipsStaffOnly',
    defaultMessage:
      'Booking without a membership is only available for a booking staff create on your behalf.',
  },
  VALIDATION__IgnoreMembershipsExcludesUserMembershipId: {
    id: 'validations.VALIDATION__IgnoreMembershipsExcludesUserMembershipId',
    defaultMessage:
      'Booking without a membership cannot also name which membership to draw the session from.',
  },
  VALIDATION__LocationOrOnlineLinkRequired: {
    id: 'validations.VALIDATION__LocationOrOnlineLinkRequired',
    defaultMessage: 'Enter a location, or an online link for a remote session.',
  },
  VALIDATION__PastDate: {
    id: 'validations.VALIDATION__PastDate',
    defaultMessage: 'Pick a date that has not already passed.',
  },
  // Live test 5 / N13 — a pause covers a cycle that has started (or on its
  // first day); a window before the cycle's start pauses nothing.
  VALIDATION__PauseBeforeMembershipStart: {
    id: 'validations.VALIDATION__PauseBeforeMembershipStart',
    defaultMessage: "A pause can't start before the membership starts.",
  },
  // Ruling AG (membership-renewal plan) — "Schedule activation" only on a
  // day of the current cycle the server accepts (the row's activationWindow).
  VALIDATION__ActivationOutsideCycle: {
    id: 'validations.VALIDATION__ActivationOutsideCycle',
    defaultMessage: 'Pick a day inside the current cycle, before it ends.',
  },
  // Task 3 (membership-renewal plan) — an early "Renew same" starts on the
  // next visit day, inside the current cycle: from its start, before its end.
  VALIDATION__RenewalStartOutsideCycle: {
    id: 'validations.VALIDATION__RenewalStartOutsideCycle',
    defaultMessage:
      'Pick a day from the start of the current cycle up to the day before it ends.',
  },
  // Task 9 (membership-renewal plan) — "Reduce to" on an early end: never
  // below what was collected (money back is a refund), never above the price
  // still in effect. The server refuses both too.
  VALIDATION__ReducedBelowCollected: {
    id: 'validations.VALIDATION__ReducedBelowCollected',
    defaultMessage:
      'The amount cannot be lower than what was already collected for this cycle.',
  },
  VALIDATION__ReducedAboveInEffect: {
    id: 'validations.VALIDATION__ReducedAboveInEffect',
    defaultMessage:
      'The amount cannot be higher than the price still in effect for this cycle.',
  },
  VALIDATION__PastDateTime: {
    id: 'validations.VALIDATION__PastDateTime',
    defaultMessage: 'Pick a date and time that has not already passed.',
  },
  // Thrown by the single-copy restore guard (an entity hook with no request
  // locale in reach) and translated FE-side by `useValidationTranslation`.
  VALIDATION__OrganizationAlreadyHasThisCopy: {
    id: 'validations.VALIDATION__OrganizationAlreadyHasThisCopy',
    defaultMessage:
      'This organization already has a copy of this content. Delete that copy before restoring this one.',
  },
  // Task 2.1 (Profits & Expenses, code-review finding #1, 2026-09-27) — the
  // CREATE finance-entry DTO's net-vs-gross check, resolved by the global
  // `ZodFilter` off this catalog.
  VALIDATION__NetAboveGross: {
    id: 'validations.VALIDATION__NetAboveGross',
    defaultMessage: 'The net amount cannot be greater than the gross amount.',
  },
  // Rebrand v2 (spec §9, MV2-599) — the org and corp rebrand routes refuse a
  // NEW choice of a background tile that is not released yet
  // (`rebrand-background.validation.ts`). It travels as the 422's `message`,
  // next to the id itself as its `code`.
  VALIDATION__BackgroundNotAvailable: {
    id: 'validations.VALIDATION__BackgroundNotAvailable',
    defaultMessage: 'This background is not available yet.',
  },
});

export type ValidationMessagesDescriptor = typeof messages;
export default messages;
